import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  importProvidersFrom,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatRadioModule } from '@angular/material/radio';

import { HttpClientModule, provideHttpClient } from '@angular/common/http';
import { NgxEchartsModule } from 'ngx-echarts';
import { KeycloakService } from 'keycloak-angular';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { SideNavbarComponent } from '@shared-lib/chart-components/side-navbar/side-navbar.component';
import { ThemeService } from '@shared-lib/foundry-shared/theme.service';
import { SpricedFooterComponent } from './modules/spriced-footer/spriced-footer.component';

import { environment } from '../environments/environment';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-root',
  providers: [HttpClientModule],
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatInputModule,
    MatRadioModule,
    SideNavbarComponent,
    SpricedFooterComponent,
    NgxEchartsModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  data: any;
  showSpricedFooter = false;
  private readonly destroyRef = inject(DestroyRef);
  private readonly spricedRoutes = new Set([
    '/approvals',
    '/approval-workflows',
    '/chain-list',
    '/workflow-chain',
    '/price-recommendation',
    '/user-access',
    '/domain-catalogs',
    '/customer-catalogs',
    '/price-list-catalogs',
    '/deam-service',
    '/audit',
    '/ttm',
    '/ttm-list',
  ]);

  constructor(
    private keycloakService: KeycloakService,
    private router: Router,
    private titleService: Title,
    private themeService: ThemeService
  ) {
    this.init();
    this.updateSpricedFooter(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.updateSpricedFooter(event.urlAfterRedirects));
  }

  ngOnInit(): void {
    this.setTabAndIcon();
    this.initTheme();
  }

  /**
   * Initialize the active theme.
   * Priority: user's saved theme (localStorage) > environment default > 'default'.
   * Custom overrides (from Custom Theme tab) are layered on top automatically.
   * Dashboard JSONs may still override this via "theme" / "themeOverrides".
   */
  private initTheme(): void {
    const fallback = (environment as any).THEME_NAME || 'default';
    this.themeService.loadManifest().subscribe();
    this.themeService.initFromPersistence(fallback);
  }

  private setTabAndIcon(): void {
    if (environment.TAB_NAME) {
      this.titleService.setTitle(environment.TAB_NAME);
    }
    
    if (environment.ICON_URL) {
      let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.type = 'image/x-icon';
        link.rel = 'icon';
        document.getElementsByTagName('head')[0].appendChild(link);
      }
      link.href = environment.ICON_URL;
    }
  }

  async init() {
    const loggedIn = await this.keycloakService.isLoggedIn();
    if (!loggedIn && !this.router.url.startsWith('/session-expired')) {
      this.keycloakService.login();
    }
  }

  logout(): void {
    this.keycloakService.logout();
  }

  private updateSpricedFooter(url: string): void {
    const path = `/${String(url || '').split('?')[0].split('#')[0].replace(/^\/+|\/+$/g, '')}`;
    this.showSpricedFooter = this.spricedRoutes.has(path);
    document.documentElement.classList.toggle('spriced-footer-active', this.showSpricedFooter);
    document.body.classList.toggle('spriced-footer-active', this.showSpricedFooter);
  }
}
